import { Row, Col, Image, Card, Spinner, Modal } from "react-bootstrap";
import { Download } from "react-bootstrap-icons";
import jsPDF from "jspdf";
import React from "react";
import { AbnormalVehicle, ReportData, VehicleCost, AbnormalVehicleInfo, ItemCountInfo } from "../../model/Report";
import { vehicleTypeImage, getWarningList, WarningMessageInfo, tirePositionNumber } from '../../utils/define';
import { ReactComponent as TruckIcon } from '../../assets/img/icon_truck_white.svg';
import { PressureIcon } from '../../components/companyInfo/icons/PressureIcon';
import { WarningType } from "../../components/companyInfo/icons/WarningType";
import { TemperatureIcon } from "../../components/companyInfo/icons/TemperatureIcon";
import "./style.css"
import { toCanvas } from 'html-to-image';

const MAX_WIDTH = 1447;
const VEHICLE_COST_SECTION_MAX_WIDTH = 1300;
const REPORT_SUMMARY_SECTION_MAX_WIDTH = 1300;
const COST_IMPACT_SECTION_MAX_WIDTH = 1300;

const REPORT_TITLE_SUMMARY = "サマリー";
const OnePageMaxAbnormalVehicle = 36;
const OnePageMaxAbnormalVehicleWithCostImpact = 36;
const OnePageMaxAbnormalVehicleWithVehicleCost = 18;

export type OutputMonth = {
    year: number,
    month: number
}

type OutputTerm = { start: string, end: string };
const initialOutputTerm: OutputTerm = { start: "", end: "" };

function formatDate(date: Date) {
    if(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // 月は0から始まるので+1
        const day = String(date.getDate()).padStart(2, '0'); // 1桁の日付を2桁にする
        return `${year}/${month}/${day}`;
    } else {
        return "問題なし";
    }

}

const getOutputTerm = (outputMonth: OutputMonth): OutputTerm => {
    const outputDate = new Date(outputMonth.year, outputMonth.month - 1);
    const currentDate = new Date();
    const start = formatDate(outputDate);
    let end = "";
    if (outputDate.getMonth() === currentDate.getMonth()) {
        // 出力対象月が当月の場合、昨日までのデータを取得する。
        currentDate.setDate(currentDate.getDate() - 1);
        end = formatDate(currentDate);
    } else {
        outputDate.setDate(1);
        outputDate.setMonth(outputDate.getMonth() + 1);
        outputDate.setDate(outputDate.getDate() - 1);
        end = formatDate(outputDate);
    }

    return { start: start, end: end };
};

/**
 * 設定する文字列が対象のエレメントに入るフォントサイズを取得する
 * @param targetItemClass 対象のエレメントのクラス名
 * @param vehicleName 設定する文字列
 * @param defaultFontSize デフォルトのフォントサイズ。設定が無かった場合、17
 * @returns 設定する文字列が対象のエレメントに入るフォントサイズ
 */
const calcFontSize = (targetItemClass:string, vehicleName:string, defaultFontSize?:number):Number => {
    const targetItemElement = document.querySelector(targetItemClass);
    const itemWidth = (targetItemElement?.clientWidth ?? 325) - 24;
    
    let span = document.createElement('span');
    span.style.position = 'absolute';
    span.style.top = '-1000px';
    span.style.left = '-1000px';
    span.style.whiteSpace = 'nowrap';
    span.innerHTML = vehicleName;
    
    let fontWidth = 0;
    let fontSize = defaultFontSize ?? 17;
    do{
        fontSize--;
        span.style.fontSize = `${fontSize}px`;
        document.body.appendChild(span)
        fontWidth = span.clientWidth;
        span.parentElement?.removeChild(span);
    } while(itemWidth < fontWidth);

    return fontSize;
}

type ReportHeaderProp = {
    outputType: string
    company: string
    outputMonth: OutputMonth
}
const ReportHeader = (props:ReportHeaderProp) => {
    const [outputTerm, setOutputTerm] = React.useState(initialOutputTerm);
    React.useEffect(() => {
        setOutputTerm(getOutputTerm(props.outputMonth));
    }, [props]);

    return (
        <>
            <div style={{ display: "flex", backgroundColor: "black", color: "white", padding: "10pt", fontWeight: "bold", fontSize: "20pt", width: '1447px'}}>
                <div style={{width:"25%", textAlign: "left", fontSize: "13pt"}}>月次タイヤレポート/{props.outputType}</div>
                <div style={{width:"50%", textAlign: "center"}}>{props.company}</div>
                <div style={{width:"25%", textAlign: "right", fontSize: "13pt"}}>{outputTerm.start} - {outputTerm.end}</div>
            </div>
        </>
    );
}

type ReportFooterProps = {
    contactName: string;
    contactPhone: string;
};

const ReportFooter = ({ contactName, contactPhone }: ReportFooterProps) => {
    return (
        <div style={{ display:"flex", fontSize: "20pt", textAlign: "center", justifyContent: "center", padding: "10pt", backgroundColor: "#DEDEDE", height: "40pt", marginBottom: "0pt", width: "100%", alignItems: "center" }}>
            <div style={{ marginRight: "10pt", alignItems: "center" }}>
                <Image src={"/images/footer_telephone.svg"} />
                <span>メンテナンスのご用命はこちらまで</span>
            </div>
                <div style={{ backgroundColor: "white", borderRadius: "2pt", padding: "3pt 10pt", fontSize: "18pt", marginRight: "10pt", minHeight: "15pt", minWidth: "150pt" }}>
                {contactName || ""} {contactPhone || ""}
            </div>
        </div>
    );
}


type SummaryAbnormalVehicleIconProps = {
    status?: number | undefined
    style?: React.CSSProperties | undefined
}
const SummaryAbnormalVehicleIcon = (props: SummaryAbnormalVehicleIconProps) => {
    return (
        <span>
            {(() => {
                if (props.status) {
                    if (props.status === 1) {
                        return <Image src={"/images/icon_warning.svg"} style={props.style} />;
                    } else {
                        return <Image src={"/images/icon_alert.svg"} style={props.style} />;
                    }
                } else {
                    return <Image src={"/images/icon_nominal.svg"} style={props.style} />;
                }
            })()}
        </span>
    );
}

type AbnormalVehicleListProps = {
    abnormalVehicleList: AbnormalVehicle[]
};
const AbnormalVehicleList = (props: AbnormalVehicleListProps) => {
    const [vehicleNameFontSizeList, setVehicleNameFontSizeList] = React.useState<Number[]>([]);
    React.useEffect(() => {
        let fontSizeList:Number[] = [];
        props.abnormalVehicleList.map((abnormalVehicle) => {
            const fontSize = calcFontSize(".abnormalVehicleVehicleName", abnormalVehicle.vehicle_name, 24);
            fontSizeList.push(fontSize);
        });
        setVehicleNameFontSizeList(fontSizeList);
    }, []);

    return (
        <>
            <Row style={{ paddingTop: "6pt", paddingBottom: "6pt", borderBottom: "solid 1px #DEDEDE" }}>
                <Col sm={3} >車両ナンバー</Col>
                <Col sm={5} >車両名</Col>
                <Col sm={2} >空気圧</Col>
                <Col sm={2} >温度</Col>
            </Row>
            {props.abnormalVehicleList.map((abnormalVehicle, index) =>
                <Row style={{ paddingTop: "6pt", paddingBottom: "6pt" }} key={index}>
                    <Col sm={3} style={{ fontWeight: "bold", alignItems: "center" }}>
                        <Image src={"/images/icon_truck.svg"} width={24} height={24} />{' '}
                        {abnormalVehicle.vehicle_number}
                    </Col>
                    <Col sm={5} style={{ alignItems: "center", fontSize: `${vehicleNameFontSizeList[index]}px` }} className="abnormalVehicleVehicleName">{abnormalVehicle.vehicle_name}</Col>
                    <Col sm={2} style={{ alignItems: "center", fontSize: "16pt" }}>
                        <SummaryAbnormalVehicleIcon status={abnormalVehicle.pressure_status} style={{ width: "10pt", height: "10pt" }} />{' '}
                        {(abnormalVehicle.pressure_abnormality_date != null) ? formatDate(new Date(abnormalVehicle.pressure_abnormality_date)) : "問題なし"}
                    </Col>
                    <Col sm={2} style={{ alignItems: "center", fontSize: "16pt" }}>
                        <SummaryAbnormalVehicleIcon status={abnormalVehicle.temperature_status} style={{ width: "10pt", height: "10pt" }} />{' '}
                        {(abnormalVehicle.temperature_abnormality_date != null) ? formatDate(new Date(abnormalVehicle.temperature_abnormality_date)) : "問題なし"}
                    </Col>
                </Row>
            )}
        </>
    );
}

type ReportSummarySectionProps = {
    abnormalVehicleList: AbnormalVehicle[]
}
const ReportSummarySection = (props: ReportSummarySectionProps) => {
    const abnormalVehicleList = props.abnormalVehicleList.length > 0 ? 
        <AbnormalVehicleList abnormalVehicleList={props.abnormalVehicleList} /> : 
        <>異常を検知した車両はありません。</>;

    return (
        <div style={{ padding: `30px ${(MAX_WIDTH-REPORT_SUMMARY_SECTION_MAX_WIDTH)/2}px`, backgroundColor: "#DEDEDE", whiteSpace: "nowrap", width: `${MAX_WIDTH}px` }}>
            <div style={{ fontWeight: "bold", fontSize: "20pt", marginBottom: "5pt" }}>当月の異常を検知した車両リスト</div>
            <div style={{ width: `${REPORT_SUMMARY_SECTION_MAX_WIDTH}px`, fontSize: "18pt", backgroundColor: "white", borderColor: "Gray", paddingLeft: "10pt", paddingRight: "10pt" }}>
                {abnormalVehicleList}
            </div>
        </div>
    );
}


type CostImpactSectionProps = {
    costImpact: number
}

const CostImpactSection = (props: CostImpactSectionProps) => {
    return (
        <div style={{ padding: `30px ${(MAX_WIDTH-COST_IMPACT_SECTION_MAX_WIDTH)/2}px` }}>
            <div style={{ fontWeight: "bold", fontSize: "20pt", marginBottom: "10px" }}>当月のコストインパクト</div>
            <div style={{ border: "2px #DEDEDE solid", backgroundColor: "white", padding: "20px", width: `${COST_IMPACT_SECTION_MAX_WIDTH}px` }}>
                <Row>
                    <Col sm={8} style={{ fontSize: "20pt", fontWeight: "bold", alignItems: "center" }}>
                        <Image src={"/images/cost_impact_burndown.svg"} style={{ width: "24pt", height: "24pt" }} />{' '}
                        適正空気圧で運行した場合の燃料費削減額(円/月)
                    </Col>
                    <Col sm={4} style={{ textAlign: "right" }}>
                        <span style={{ fontSize: "26pt", fontWeight: "bold" }}>{props.costImpact.toLocaleString()}</span>
                        <span style={{ fontSize: "20pt", fontWeight: "bold" }}>円/月</span>
                    </Col>
                </Row>
            </div>
        </div>
    );
}

type VehicleCostSectionProps = {
    vehicleCostList: VehicleCost[]
}

const VehicleCostSection = (props: VehicleCostSectionProps) => {
    const [vehicleNameFontSizeList, setVehicleNameFontSizeList] = React.useState<Number[]>([]);
    React.useEffect(() => {
        let fontSizeList:Number[] = [];
        props.vehicleCostList.map((vehicleCost) => {
            const fontSize = calcFontSize(".vehicleCostVehicleName", vehicleCost.vehicle_name);
            fontSizeList.push(fontSize);
        });
        setVehicleNameFontSizeList(fontSizeList);
    }, []);

    return (
        <div style={{ padding: `30px ${(MAX_WIDTH-VEHICLE_COST_SECTION_MAX_WIDTH)/2}px`, whiteSpace: "nowrap" }}>
            <div style={{marginBottom: "5px"}}>
                <span style={{ fontWeight: "bold", fontSize: "20pt" }}>当月の車両別燃費</span>
                <span style={{ fontSize: "16pt" }}> ※月末時点での空気圧から算出した想定燃費</span>
            </div>
            <div style={{ fontSize: "15pt", backgroundColor: "white", border: "2px #DEDEDE solid", width: `${VEHICLE_COST_SECTION_MAX_WIDTH}px` }}>
                <Row style={{ paddingTop: "6pt", paddingBottom: "6pt", borderBottom: "solid 1px #DEDEDE", width: `${VEHICLE_COST_SECTION_MAX_WIDTH}px`, marginLeft: "0px" }}>
                    <Col sm={1} >No.</Col>
                    <Col sm={2} >車両ナンバー</Col>
                    <Col sm={3} >車両名</Col>
                    <Col sm={2} style={{fontSize:"14pt"}}>当月の燃費(km/L)</Col>
                    <Col sm={2} style={{fontSize:"14pt"}}>理想的な燃費</Col>
                    <Col sm={1} style={{fontSize:"14pt"}}>燃費差分</Col>
                    <Col sm={1} style={{fontSize:"14pt"}}>削減額</Col>
                </Row>
                {props.vehicleCostList.map((vehicleCost, index) =>
                    <Row style={{ paddingTop: "6pt", paddingBottom: "6pt", width: `${VEHICLE_COST_SECTION_MAX_WIDTH}px`, marginLeft: "0px" }} key={index}>
                        <Col sm={1}>{index + 1}</Col>
                        <Col sm={2}>
                            {vehicleCost.vehicle_number}
                        </Col>
                        <Col sm={3} className="vehicleCostVehicleName" style={{fontSize: `${vehicleNameFontSizeList[index]}px`}}>
                            {vehicleCost.vehicle_name}
                        </Col>
                        <Col sm={2}>
                            {vehicleCost.fuel_economy}
                        </Col>
                        <Col sm={2}>
                            {vehicleCost.ideal_fuel_economy}
                        </Col>
                        <Col sm={1}>
                            {vehicleCost.diff}
                        </Col>
                        <Col sm={1}>
                            {vehicleCost.savings.toLocaleString()}円
                        </Col>
                    </Row>
                )}
            </div>
        </div>
    );
}


const formatItemCountInfoChipDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear().toString().slice(2, 4);
    const month = (date.getMonth()+1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hour = date.getHours().toString().padStart(2, '0');
    const minute = date.getMinutes().toString().padStart(2, '0');
    return `${year}/${month}/${day} ${hour}:${minute}`;
}

type ItemCountInfoChipDateProps = {
    date: string
}

const ItemCountInfoChipDate = (props: ItemCountInfoChipDateProps) => {
    return (
        <div style={{backgroundColor: "white", width: "130pt", borderRadius: "100pt", padding: "2pt 8pt", textAlign: "center", margin: "3pt"}}>
            {formatItemCountInfoChipDate(props.date)}
        </div>
    );
}

type ItemCountInfoChipProps = {
    title: string
    type: string
    data: ItemCountInfo
}

const ItemCountInfoChip = (props: ItemCountInfoChipProps) => {
    return (
        <Row style={{
            backgroundColor: props.data.count > 0 ? (props.type === "alert" ? "red" : "yellow") : "white",
            color: props.data.count > 0 ? (props.type === "alert" ? "white" : "black") : "black",
            border: "solid 1px #DEDEDE",
            paddingTop: "8pt", paddingBottom: "8pt"
        }}>
            <div style={{display:"flex"}}>
                <div style={{width: "250pt"}}>{props.title}</div>
                <div style={{width: "40pt", textAlign: "right"}}>{props.data.count}</div>
            </div>
            <div style={{color: "black", fontSize: "16pt"}}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "auto 10pt"}}>
                    {props.data.date_list.length > 0 ? <ItemCountInfoChipDate date={props.data.date_list[0]} /> : <div style={{ width: "75pt"}}></div>}
                    {props.data.date_list.length > 1 ? <ItemCountInfoChipDate date={props.data.date_list[1]} /> : <div style={{ width: "75pt"}}></div>}
                </div>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "auto 10pt"}}>
                    {props.data.date_list.length > 2 ? <ItemCountInfoChipDate date={props.data.date_list[2]} /> : <div style={{ width: "75pt"}}></div>}
                    {props.data.date_list.length > 3 ?<ItemCountInfoChipDate date={ props.data.date_list[3]} /> : <div style={{ width: "75pt"}}></div>}
                </div>
            </div>
        </Row>
    );
}

type WarningMessageProps = {
    data:AbnormalVehicleInfo
}

const WarningMessage = (props:WarningMessageProps) => {
    const warnings:WarningMessageInfo[] = getWarningList(props.data);
    const getWarningIcon = (dataKind:string, type:string) => {
        if(dataKind === "pressure" && type === "error") {
            return <PressureIcon type={WarningType.Error} />
        } else if(dataKind === "pressure" && type === "warning") {
            return <PressureIcon type={WarningType.Warning} />
        } else if(dataKind === "temperature" && type === "error") {
            return <TemperatureIcon type={WarningType.Error} />
        } else {
            return <TemperatureIcon type={WarningType.Warning} />
        }
    }

    return (
        <div style={{backgroundColor: "white", color: "black"}}>
            {warnings.map((warning: WarningMessageInfo, index:number) => {
                return (
                    <div style={{height: "32pt", lineHeight: "32pt", margin: "0pt", paddingLeft: "16pt", borderBottom: "1px solid black"}} key={index}>
                        <span style={{marginRight: "10pt"}}>
                            { getWarningIcon(warning.dataKind, warning.type) }
                        </span>
                        <span>{ warning.message }</span>
                    </div>
                );
            })
        }
        </div>

    );
}

type AbnormalVehicleInfoSectionProps = {
    abnormalVehicleInfo: AbnormalVehicleInfo[]
};

const AbnormalVehicleInfoSection = (props:AbnormalVehicleInfoSectionProps) => {
    return (
        <div className="card-wrapper" style={{margin: "10pt auto", paddingTop: "30px", paddingBottom: "30px", whiteSpace: "nowrap"}}>
        {props.abnormalVehicleInfo.map((data:AbnormalVehicleInfo, index:number) => {
            return (                
                <Card  key={index} style={{fontSize: "20pt", backgroundColor: "#343434", color: "white", margin: "10pt 30pt"}}>
                    <Card.Title style={{display: "flex", margin: "8pt 16pt"}}>
                        <div style={{width: "50%", color:"white"}}>
                            <TruckIcon style={{fill:"white", width: "24pt", height: "24pt"}} />
                            {data.vehicle_number}/{tirePositionNumber((data.vehicle_type), data.axle_number, data.lateral_position)}
                            <p style={{marginLeft: "24pt"}}>{data.vehicle_name}</p>
                        </div>
                        <div style={{fontSize: "15pt", marginRight: "10pt", height: "18pt", lineHeight: "18pt"}}>
                            <Image src={"/images/icon_alert.svg"} width={18} height={18} />
                            問題あり(すぐに点検必要){' '}
                            <Image src={"/images/icon_warning.svg"} width={18} height={18} />
                            問題あり(近々メンテナンス必要)
                        </div>
                    </Card.Title>
                    <Card.Body style={{margin: "8pt 40pt 30pt 40pt"}}>
                        <Row style={{display: "flex", justifyContent: "space-around", margin: "0 auto" }}>
                            <Col sm={3} style={{display: "flex", justifyContent: "center", alignItems: "center", padding: "0"}}>
                            {vehicleTypeImage(data.vehicle_type, data.axle_number, data.lateral_position)}
                            </Col>
                            <Col sm={4}>
                                <div>空気圧異常</div>
                                <div style={{padding: "0px", borderRadius: "100pt"}}>
                                    <ItemCountInfoChip title={"空気圧異常"} type="alert" data={data.pressure_info.pressure_alert} />
                                    <ItemCountInfoChip title={"スローパンク"} type="alert" data={data.pressure_info.slow_punc} />
                                    <ItemCountInfoChip title={"空気圧低下(自然減圧)"} type="warning" data={data.pressure_info.pressure_drop} />
                                </div>
                            </Col>
                            <Col sm={4}>
                                <div>温度異常</div>
                                <ItemCountInfoChip title={"温度異常"} type="alert" data={data.temperature_info.temperature_alert} />
                                <ItemCountInfoChip title={"温度注意"} type="warning" data={data.temperature_info.temperature_caution} />
                            </Col>
                        </Row>
                    </Card.Body>
                    <Card.Footer style={{backgroundColor: "white", padding: "0pt"}}>
                        <WarningMessage data={data} />
                    </Card.Footer>
                </Card>
            );
        })}
        </div>
    );
}

type LoadingSpinnerProps = {
    show: boolean
}
const LoadingSpinner = (props:LoadingSpinnerProps) => {
    return (
        <div style={{
            display: props.show ? "flex" : "none",
            position: "fixed",
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center'}}
        >
            <div style={{position: "absolute", justifyContent: "center", alignItems: "center", }}>
                <Spinner />
                <div>
                    <span style={{color: "white"}}>Downloading...</span>
                </div>

            </div>
        </div>
    );
}

const generatePdf = async (
    props: PDFFilenameProps,
    headerRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
    reportRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
    footerRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
    beforeDownloadHandler: () => {},
    afterDownloadHandler: () => {}
) => {
    beforeDownloadHandler();
    let doc = new jsPDF({
        orientation: 'portrait', // 'portrait' または 'landscape'（縦向きまたは横向き）
        unit: 'pt',              // 単位。'pt', 'mm', 'cm', 'in' の中から選択
        format: 'a4'             // 用紙サイズ。'a4', 'letter', 'legal' などを指定
    });

    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const scaleFactor = 3;
    const headerHeight = 33; // ヘッダーの高さを固定
    const footerHeight = 35; // フッターの高さを固定
    let pdfSuffix = 1;

    for (let index = 0; index < reportRefs.current.length; index++) {
        const headerElement = headerRefs.current[index];
        const reportElement = reportRefs.current[index];
        const footerElement = footerRefs.current[index];

        // ヘッダーの描画
        if (headerElement) {
            const headerCanvas = await toCanvas(headerElement, { pixelRatio: scaleFactor });
            const headerImgData = headerCanvas.toDataURL('image/png');
            doc.addImage(headerImgData, 'JPEG', 0, 0, pageWidth, headerHeight, undefined, 'FAST');
        }

        // レポートの描画
        if (reportElement) {
            const diff = pageWidth / reportElement.offsetWidth * 0.9;
            if (headerElement?.textContent?.includes('車両異常情報')) {
                const cardElements = Array.from(reportElement.querySelectorAll('.card-wrapper'));
                let yPosition = 10;
                const cardWidth = pageWidth * 0.9; // 横幅を10%縮小
                const xOffset = (pageWidth - cardWidth) / 2; // 左右の余白を5%ずつ

                for (const cardElement of cardElements) {
                    const cardCanvas = await toCanvas(cardElement as HTMLElement, { pixelRatio: diff * scaleFactor });
                    const cardImgData = cardCanvas.toDataURL('image/png');
                    const cardHeight = (cardCanvas.height / cardCanvas.width) * cardWidth; // cardの縦横比を保持してサイズ調整

                    if (yPosition + cardHeight + footerHeight > pageHeight) {
                        if(pageHeight - (yPosition + cardHeight + footerHeight) > 5){
                            doc.addPage();
                        }

                        // 新ページにヘッダーを描画
                        if (headerElement) {
                            const headerCanvas = await toCanvas(headerElement, { pixelRatio: scaleFactor });
                            const headerImgData = headerCanvas.toDataURL('image/png');
                            doc.addImage(headerImgData, 'JPEG', 0, 0, pageWidth, headerHeight, undefined, 'FAST');
                        }
                    } else {
                        yPosition += 20;
                    }
                    // 横幅をcardWidthに設定し、xOffsetで左右中央揃え
                    doc.addImage(cardImgData, 'JPEG', xOffset, yPosition, cardWidth , cardHeight, undefined, 'FAST');
                    yPosition += cardHeight;
                }
            } else {
                const reportCanvas = await toCanvas(reportElement, { pixelRatio: diff * scaleFactor });
                let remainingHeight = reportCanvas.height;
                let yPosition = 30;

                const tempCanvas = document.createElement('canvas');
                const tempContext = tempCanvas.getContext('2d');
                tempCanvas.width = reportCanvas.width;

                while (remainingHeight > 0) {
                    const partHeight = Math.min(remainingHeight, (pageHeight - footerHeight) * scaleFactor - yPosition * scaleFactor);
                    tempCanvas.height = partHeight;

                    tempContext?.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
                    tempContext?.drawImage(
                        reportCanvas,
                        0,
                        reportCanvas.height - remainingHeight,
                        reportCanvas.width,
                        partHeight,
                        0,
                        0,
                        tempCanvas.width,
                        tempCanvas.height
                    );

                    const partImgData = tempCanvas.toDataURL('image/png');
                    doc.addImage(partImgData, 'JPEG', 0, yPosition, pageWidth, partHeight / scaleFactor + 40, undefined, 'FAST');
                    

                    if (footerElement) {
                        const footerCanvas = await toCanvas(footerElement, { pixelRatio: scaleFactor });
                        const footerImgData = footerCanvas.toDataURL('image/png');
                        doc.addImage(footerImgData, 'JPEG', 0, pageHeight - footerHeight, pageWidth, footerHeight, undefined, 'FAST');
                    }

                    remainingHeight -= partHeight;

                    if (remainingHeight > 10) {
                        yPosition += 5;
                        doc.addPage();
                    }

                    if (headerElement) {
                        const headerCanvas = await toCanvas(headerElement, { pixelRatio: scaleFactor });
                        const headerImgData = headerCanvas.toDataURL('image/png');
                        doc.addImage(headerImgData, 'JPEG', 0, 0, pageWidth, headerHeight, undefined, 'FAST');
                    }
                }
            }
        }

        if (footerElement) {
            const footerCanvas = await toCanvas(footerElement, { pixelRatio: scaleFactor });        
            const footerImgData = footerCanvas.toDataURL('image/png');
            doc.addImage(footerImgData, 'JPEG', 0, pageHeight - footerHeight, pageWidth, footerHeight, undefined, 'FAST');
        }

        // 30ページごとに出力
        if (doc.internal.pages.length > 30 && reportRefs.current.length > index + 1) {
            doc.save(`${props.company}タイヤレポート-${props.outputMonth.year}-${props.outputMonth.month}-${pdfSuffix}.pdf`);
            pdfSuffix++;
            doc = new jsPDF({
                orientation: 'portrait', // 'portrait' または 'landscape'（縦向きまたは横向き）
                unit: 'pt',              // 単位。'pt', 'mm', 'cm', 'in' の中から選択
                format: 'a4'             // 用紙サイズ。'a4', 'letter', 'legal' などを指定
            });
        } else if (reportRefs.current.length > index + 1) {
            doc.addPage();
        }
    }
    
    if (doc.internal.pages.length > 1) {
        doc.save(`${props.company}タイヤレポート-${props.outputMonth.year}-${props.outputMonth.month}-${pdfSuffix}.pdf`);
    }    
    afterDownloadHandler();
};

const REPORT_TITLE_ABNORMAL_VEHICLE_INFO_TITLE = (pageSize:number, pageNum:number) => {return `車両異常情報[${pageNum}/${pageSize}]`};

const sliceReportData = <T,>(datas:Array<T>, maxItemsPerChunk:number)  => {
    let result = [];
    for (let i = 0; i < datas.length; i += maxItemsPerChunk) {
        result.push(datas.slice(i, i + maxItemsPerChunk));
    }
    return result;
}

/**
 * 車両異常情報のリストをタイヤ番号順にソート
 * @param abnormalVehicleInfo 車両異常情報のリスト
 * @returns タイヤ番号順にソートされた車両異常情報のリスト
 */
const sortAbnormalVehicleInfo = (abnormalVehicleInfo:AbnormalVehicleInfo[]):AbnormalVehicleInfo[] => {
    return abnormalVehicleInfo.sort((a:AbnormalVehicleInfo, b:AbnormalVehicleInfo) => {
        if(a.vehicle_number === b.vehicle_number) {
            return tirePositionNumber(a.vehicle_type, a.axle_number, a.lateral_position) - tirePositionNumber(b.vehicle_type, b.axle_number, b.lateral_position)
        } else {
            return 0;
        }
    });
}

type ReportFileName = {
    company: string
    outputMonth: OutputMonth
}

type ReportPageData = {
    title: string
    company: string
    abnormalVehicleList?: AbnormalVehicle[],
    costImpact?: number,
    vehicleCostList?: VehicleCost[],
    abnormalVehicleInfoList?: AbnormalVehicleInfo[]
}

const PDFViewer = (props: PDFViewerProps) => {
    const reportRefs = React.useRef<Array<HTMLDivElement | null>>([]);
    const headerRefs = React.useRef<Array<HTMLDivElement | null>>([]);
    const footerRefs = React.useRef<Array<HTMLDivElement | null>>([]);

    const [ fileName, setFileName ] = React.useState<ReportFileName>({
        company: "",
        outputMonth: props.outputMonth
    });

    const [ isDownloadingShow, setIsDownloadingShow ] = React.useState(false);

    const [ pageDatas, setPageDatas ] = React.useState<ReportPageData[]>([]);
    React.useEffect(() => {
        let pageIndex = 0;
        let reportDatas: ReportPageData[] = [];        

        if (props.datas.length === 1) {
            setFileName({
                company: `${props.datas[0].client.company}-`,
                outputMonth: props.outputMonth
            })
        };

        props.datas.map((data:ReportData, index:number) => {
            data.abnormal_vehicle_info = sortAbnormalVehicleInfo(data.abnormal_vehicle_info);

            // 「異常のある車両」のリストが1ページ内に収まる個数で分割
            const abnormalVehicleList: AbnormalVehicle[][] = data.abnormal_vehicle
                ? sliceReportData(data.abnormal_vehicle, OnePageMaxAbnormalVehicle)
                : [[]];

            if (data.abnormal_vehicle && data.abnormal_vehicle.length === 0){
                // 異常車両がない場合
                // 異常のある車両とコストインパクトと車両別燃費が入る
                const reportData = {
                    title: REPORT_TITLE_SUMMARY,
                    company: data.client.company,
                    abnormalVehicleList: [],
                    costImpact: data.cost_impact,
                    vehicleCostList: data.vehicle_cost ? data.vehicle_cost : []
                };
                reportDatas.push(reportData);
                pageIndex++;

                const avCountLastPage = reportDatas[pageIndex-1].abnormalVehicleList!.length - 1;                

                // 異常のある車両の最後のページにコストインパクトが入る場合（最後に車両別燃費が入る余地がある）
                reportDatas[pageIndex-1].costImpact = data.cost_impact;

                if(avCountLastPage < OnePageMaxAbnormalVehicleWithVehicleCost) {
                    // 異常のある車両とコストインパクトと車両別燃費が入る場合
                    reportDatas[pageIndex-1].vehicleCostList = data.vehicle_cost;
                    if(data.vehicle_cost != null) {
                        // 車両別燃費（コストインパクトが入る場合は最大33個。何行か異常のある車両のヘッダ行とデータ行が入るため、その分をマイナスする）
                        const firstPageVehicleCostListCount = 35 - avCountLastPage - 4;
                        reportDatas[pageIndex-1].vehicleCostList = data.vehicle_cost.splice(0, firstPageVehicleCostListCount);
    
                        // 1ページの最大行数36で分割する
                        const vehicleCostLists = sliceReportData(data.vehicle_cost, 38);
                        for(let vcListCount = 0; vcListCount < vehicleCostLists.length; vcListCount++) {
                            reportDatas.push({
                                title: REPORT_TITLE_SUMMARY,
                                company: data.client.company,
                                vehicleCostList: vehicleCostLists[vcListCount]
                            });
                            pageIndex++;
                        }
                    } else {
                        pageIndex++;
                    }
                }
            } else {
                // 異常車両がある場合
                // サマリ部分の作成
                // 「異常のある車両」のリストが1ページ内に収まる個数で分割
                for(let avIndex = 0; avIndex < abnormalVehicleList.length; avIndex++) {
                    const reportData = {
                        title: REPORT_TITLE_SUMMARY,
                        company: data.client.company,
                        abnormalVehicleList: abnormalVehicleList[avIndex]
                    }
                    reportDatas.push(reportData);
                    pageIndex++;
                }
                if(reportDatas[pageIndex-1].abnormalVehicleList) {
                    const avCountLastPage = reportDatas[pageIndex-1].abnormalVehicleList!.length;
                    if(avCountLastPage >= OnePageMaxAbnormalVehicleWithCostImpact) {
                        // 異常のある車両の最後のページがコストインパクトが入らない件数の場合、コストインパクトは次のページに設定する。(先頭がコストインパクトになる)
                        // 車両別燃費を分割(1ページ目はコストインパクト分を引く(24))
                        if(data.vehicle_cost !== null) {
                            const vehicleCostOnePage:VehicleCost[] = data.vehicle_cost.splice(0, 26);
                            reportDatas.push({
                                title: REPORT_TITLE_SUMMARY,
                                company: data.client.company,
                                costImpact: data.cost_impact,
                                vehicleCostList: vehicleCostOnePage
                            });
                            pageIndex++;
    
                            // 2ページ目以降
                            // 車両別燃費を分割（1ページ最大28列）
                            const vehicleCostLists = sliceReportData(data.vehicle_cost, 28);
                            for(let vcListCount = 0; vcListCount < vehicleCostLists.length; vcListCount++) {
                                reportDatas.push({
                                    title: REPORT_TITLE_SUMMARY,
                                    company: data.client.company,
                                    vehicleCostList: vehicleCostLists[vcListCount]
                                });
                                pageIndex++;
                            }
                        } else {
                            pageIndex++;
                        }
                    } else {
                        // 異常のある車両の最後のページにコストインパクトが入る場合（最後に車両別燃費が入る余地がある）
                        reportDatas[pageIndex-1].costImpact = data.cost_impact;

                        if(avCountLastPage < OnePageMaxAbnormalVehicleWithVehicleCost) {
                            // 異常のある車両とコストインパクトと車両別燃費が入る場合
                            reportDatas[pageIndex-1].vehicleCostList = data.vehicle_cost;
                            if(data.vehicle_cost !== null) {
                                // 車両別燃費（コストインパクトが入る場合は最大24個。何行か異常のある車両のヘッダ行とデータ行が入るため、その分をマイナスする）
                                const firstPageVehicleCostListCount = 32 - avCountLastPage - 4;
                                reportDatas[pageIndex-1].vehicleCostList = data.vehicle_cost.splice(0, firstPageVehicleCostListCount);
    
                                // 1ページの最大行数28で分割する
                                const vehicleCostLists = sliceReportData(data.vehicle_cost, 38);
                                for(let vcListCount = 0; vcListCount < vehicleCostLists.length; vcListCount++) {
                                    reportDatas.push({
                                        title: REPORT_TITLE_SUMMARY,
                                        company: data.client.company,
                                        vehicleCostList: vehicleCostLists[vcListCount]
                                    });
                                    pageIndex++;
                                }
                            } else {
                                pageIndex++;
                            }
                        } else {
                            if(data.vehicle_cost !== null) {
                                // 車両別燃費が入る場合
                                const vehicleCostLists = sliceReportData(data.vehicle_cost, 38);
                                for(let vcListCount = 0; vcListCount < vehicleCostLists.length; vcListCount++) {
                                    reportDatas.push({
                                        title: REPORT_TITLE_SUMMARY,
                                        company: data.client.company,
                                        vehicleCostList: vehicleCostLists[vcListCount]
                                    });
                                    pageIndex++;
                                }
                            } else {
                                pageIndex++;
                            }
                        }
                    }
                }
                // 車両異常情報を設定
                // 車両異常情報を車両番号別のリストへ変換
                let vehicleNumberSummary:{ [key: string]: AbnormalVehicleInfo[] } = {};
                data.abnormal_vehicle_info.map((abnormalVehicleInfoData:AbnormalVehicleInfo, index:number) => {
                    if(vehicleNumberSummary.hasOwnProperty(abnormalVehicleInfoData.vehicle_number)) {
                        // 車両番号のキーを持つ要素が存在する場合、その要素の配列に追加
                        vehicleNumberSummary[abnormalVehicleInfoData.vehicle_number].push(abnormalVehicleInfoData);
                    } else {
                        // 車両番号のキーを持つ要素が存在しない場合、その要素を新規作成
                        vehicleNumberSummary[abnormalVehicleInfoData.vehicle_number] = [abnormalVehicleInfoData];
                    }
                });
                // 車両番号別のリストをもとにレポートデータを作成
                Object.keys(vehicleNumberSummary).map((vehicleNumber, index) => {
                    const pagingList:AbnormalVehicleInfo[][] = sliceReportData(vehicleNumberSummary[vehicleNumber], 2);
                    pagingList.map((pageData:AbnormalVehicleInfo[], index:number) => {
                        reportDatas.push({
                            title: REPORT_TITLE_ABNORMAL_VEHICLE_INFO_TITLE(
                                vehicleNumberSummary[vehicleNumber].length,
                                index+1
                            ),
                            company: data.client.company,
                            abnormalVehicleInfoList: pageData
                        });
                        pageIndex++;
                    });
                });
            }
        });
        setPageDatas(reportDatas);
    }, [props.datas]);

    console.log(`pageDatas: ${pageDatas.length}`);

    const [show, setShow] = React.useState(false);
    const [contactName, setContactName] = React.useState("");
    const [contactPhone, setContactPhone] = React.useState("");
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    return (
        <>
            <div className="PDF-wrapper">
                <div style={{ backgroundColor: "#aaaaaa", textAlign: "right", width: `${MAX_WIDTH}px` }}>
                    <Download
                        style={{ color: "white", fontSize: "34pt" }}
                        onClick={handleShow}
                    />
                </div>
                <Modal show={show} onHide={handleClose}>
                    <Modal.Header closeButton className="ps-4 pe-4 pb-0 border-bottom-0">
                        <Modal.Title>連絡先入力</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="ps-4 pe-4">
                        <p style={{ fontSize: "12px" }}>
                            レポートに入力する連絡先を入力してください。<br />
                            未入力で「OK」をクリックすると、空白のまま表示されます。
                        </p>
                        <div className="input-wapper">
                            <label className="ms-2" htmlFor="contactName" style={{ fontSize: "12px" }}>  連絡先名</label>
                            <input id="contactName" type="text" value={contactName}onChange={(e) => setContactName(e.target.value)}/>
                        </div>
                        <div className="input-wapper">
                            <label className="ms-2" htmlFor="contactPhone" style={{ fontSize: "12px" }}>  電話番号</label>
                            <input id="contactPhone" type="text" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}/>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <span className="p-1" onClick={handleClose} style={{ cursor: "pointer", marginRight: "10px" }}>
                            キャンセル
                        </span>
                        <span className="p-1"
                        onClick={() => {
                            generatePdf(
                                fileName, 
                                headerRefs, 
                                reportRefs, 
                                footerRefs,
                                async () => setIsDownloadingShow(true),
                                async () => setIsDownloadingShow(false)
                            );  
                            handleClose();
                        }}
                        style={{ cursor: "pointer", color: "#028FFF" }}>
                            OK
                        </span>
                    </Modal.Footer>
                </Modal>
                <div style={{ backgroundColor: "black" }}>
                    <div>
                        {pageDatas.map((report: ReportPageData, index) =>
                            <div style={{ backgroundColor: "#F4F4F4", width: `${MAX_WIDTH}px`}} key={index}>
                                <div ref={(el) => (headerRefs.current[index] = el)}>
                                    <ReportHeader outputType={report.abnormalVehicleInfoList ? report.title : "サマリー"} outputMonth={props.outputMonth} company={report.company} />
                                </div>
                                <div ref={(el) => (reportRefs.current[index] = el)}>
                                    {report.abnormalVehicleList && <ReportSummarySection abnormalVehicleList={report.abnormalVehicleList} />}
                                    {report.costImpact !== undefined && report.costImpact !== null && <CostImpactSection costImpact={report.costImpact} />}
                                    {report.vehicleCostList && report.vehicleCostList.length > 0 && <VehicleCostSection vehicleCostList={report.vehicleCostList} />}
                                    {report.abnormalVehicleInfoList && <AbnormalVehicleInfoSection abnormalVehicleInfo={report.abnormalVehicleInfoList} />}
                                </div>
                                <div ref={(el) => (footerRefs.current[index] = el)} >
                                    <ReportFooter
                                        contactName={contactName}
                                        contactPhone={contactPhone}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <LoadingSpinner show={isDownloadingShow} />
            </div>
        </>
    );
}

/**
 * PDFファイル名作成情報
 */
export type PDFFilenameProps = {
    company: string             // 出力対象会社名
    outputMonth: OutputMonth    // 出力対象年月
};

/**
 * PDFビュアー出力情報
 */
export type PDFViewerProps = {
    outputMonth: OutputMonth    // 出力対象年月
    datas: ReportData[]         // 出力情報
};

export default PDFViewer;
